import React, { useCallback, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { STRINGS } from '@/constants/strings';
import { useBiometric } from '@/features/auth/hooks/use-biometric';
import type { BiometricKind } from '@/features/auth/services/biometric-auth';
import {
  AccountSecuritiesContent,
  GuardianProfileActionHeader,
} from '@/features/guardian-profile/components';
import { useGuardianStackNavigation, useScaling } from '@/hooks';
import { useSessionStore } from '@/stores';
import { guardianColors } from '@/theme/colors';

const strings = STRINGS.guardianProfile.accountSecurities;

type GuardianAccountSecuritiesContentProps = {
  tabletPanel?: boolean;
};

type SocialProviderId = 'apple.com' | 'google.com';

function getBiometricCopy(kind: BiometricKind) {
  if (kind === 'both-face-and-finger') {
    return {
      titleKind: 'Biometrics',
      subtitleKind: 'Biometrics',
      icon: 'IcFingerPrint' as const,
    };
  }
  if (kind === 'fingerprint') {
    return {
      titleKind: 'TouchID',
      subtitleKind: 'Touch ID',
      icon: 'IcFingerPrint' as const,
    };
  }
  if (kind === 'iris') {
    return {
      titleKind: 'Biometrics',
      subtitleKind: 'Biometrics',
      icon: 'IcFaceId' as const,
    };
  }
  return {
    titleKind: 'FaceID',
    subtitleKind: 'Face ID',
    icon: 'IcFaceId' as const,
  };
}

export function GuardianAccountSecuritiesContent({
  tabletPanel = false,
}: GuardianAccountSecuritiesContentProps) {
  const navigation = useGuardianStackNavigation();
  const { kind, enabled, enable, disable } = useBiometric();
  const [biometricBusy, setBiometricBusy] = useState(false);
  const providerData = useSessionStore(state => state.user?.providerData);

  const connectedProviders = useMemo(
    () => new Set((providerData ?? []).map(provider => provider.providerId)),
    [providerData],
  );
  const biometricCopy = useMemo(() => getBiometricCopy(kind), [kind]);

  const handleChangePassword = useCallback(() => {
    navigation.navigate('GuardianChangePassword');
  }, [navigation]);

  const handleToggleBiometric = useCallback(
    async (nextValue: boolean) => {
      if (Platform.OS === 'web') return;

      setBiometricBusy(true);
      try {
        if (nextValue) {
          await enable();
        } else {
          await disable();
        }
      } finally {
        setBiometricBusy(false);
      }
    },
    [disable, enable],
  );

  const isProviderConnected = useCallback(
    (providerId: SocialProviderId) => connectedProviders.has(providerId),
    [connectedProviders],
  );

  const biometric = useMemo(() => {
    if (Platform.OS === 'web') return undefined;

    return {
      icon: biometricCopy.icon,
      title: strings.biometricTitle.replace('${kind}', biometricCopy.titleKind),
      subtitle: strings.biometricSubtitle.replace('${kind}', biometricCopy.subtitleKind),
      enabled,
      disabled: biometricBusy,
      onValueChange: handleToggleBiometric,
    };
  }, [biometricBusy, biometricCopy, enabled, handleToggleBiometric]);

  return (
    <AccountSecuritiesContent
      tabletPanel={tabletPanel}
      biometric={biometric}
      appleConnected={isProviderConnected('apple.com')}
      googleConnected={isProviderConnected('google.com')}
      onChangePassword={handleChangePassword}
    />
  );
}

export function GuardianAccountSecuritiesScreen() {
  const { isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const navigation = useGuardianStackNavigation();

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <GuardianProfileActionHeader
        title={strings.title}
        isTablet={isTablet}
        topInset={insets.top}
        onBack={navigation.goBack}
      />
      <GuardianAccountSecuritiesContent tabletPanel={isTablet} />
    </Stack>
  );
}
